import { useState } from 'react';

import { getMustBeMetProtocols, getPassengerCaregivers, getSchoolContacts } from '../../api/services/MustBeMet';

import { Contact, MustBeMetProtolsQueryParams } from './types';

interface IUseMustBeMetProtocol {
  protocolOptions: ProtocolOption[];
  protocolsLoading: boolean;
  protocolsError: boolean;
  fetchMustBeMetProtocols: (queryParams: MustBeMetProtolsQueryParams) => void;
  getProtocolLabelByValue: (value?: string) => string;
  getMustBeMetContacts: (
    locationType: string,
    accountLocationId: number | string | undefined,
    placeId: string | undefined,
    riderSlug: string
  ) => Promise<{ contacts: Contact[]; defaultIndividualGroup: AuthorizedContactGroup | null }>;
  isOnMasterSchoolList: boolean;
  resetProtocolOptions: () => void;
}

interface ProtocolOption {
  label: string;
  value: string;
  instructions: string;
}

type SchoolContactsQueryParams = {
  page: number;
  perPage: number;
  accountLocationId?: string;
  placeId?: string;
};

export const useMustBeMetProtocol = (): IUseMustBeMetProtocol => {
  const [protocolOptions, setProtocolOptions] = useState<ProtocolOption[]>([]);
  const [protocolsLoading, setProtocolsLoading] = useState(true);
  const [protocolsError, setProtocolsError] = useState(false);
  const [isOnMasterSchoolList, setIsOnMasterSchoolList] = useState<boolean>(false);

  const getMustBeMetContacts = async (locationType, accountLocationId, placeId, riderSlug) => {
    setIsOnMasterSchoolList(false);

    if (locationType === 'school') {
      const queryParams = buildSchoolContactQueryParams(accountLocationId, placeId);

      const response = await getSchoolContacts(queryParams);

      if (response?.type === 'success') {
        const schoolContacts = response.data?.data?.map((contact) => {
          return { firstName: contact.name, lastName: '', mobilePhoneNumber: contact.phone };
        });

        setIsOnMasterSchoolList(true);

        return {
          contacts: schoolContacts,
          defaultIndividualGroup: {
            name: schoolContacts[0]?.firstName,
            escalationPhoneNumber: schoolContacts[0]?.mobilePhoneNumber,
            saved: true,
            verifyIdBadge: true,
            verifyRiderName: true,
            verifyRiderBirthdate: false,
          },
        };
      }
    } else if (locationType === 'other') {
      const queryParams = buildSchoolContactQueryParams(accountLocationId, placeId);

      const response = await getSchoolContacts(queryParams);

      if (response?.type === 'success') {
        return otherLocationTypeContacts(response.data?.data, riderSlug);
      }
    } else if (riderSlug) {
      const response = await getPassengerCaregivers(riderSlug);

      if (response?.type === 'success') {
        const caregiverContacts = response.data?.caregiverContacts;

        return {
          contacts: caregiverContacts,
          defaultIndividualGroup: null,
        };
      }
    }

    return {
      contacts: [],
      defaultIndividualGroup: null,
    };
  };

  const fetchMustBeMetProtocols = async (locationId) => {
    setProtocolsLoading(true);
    setProtocolsError(false);
    try {
      const response = await getMustBeMetProtocols(locationId);

      if (response?.type === 'success') {
        setProtocolOptions(response.data.locationProtocols);
      } else {
        setProtocolsError(true);
      }
    } catch {
      setProtocolsError(true);
    } finally {
      setProtocolsLoading(false);
    }
  };

  const getProtocolLabelByValue = (value?: string) => {
    if (!value) {
      return '';
    }
    const protocolMatch = protocolOptions.find((protocol) => protocol.value === value);

    return protocolMatch?.label || '';
  };

  const resetProtocolOptions = () => {
    setProtocolOptions([]);
    setProtocolsLoading(false);
    setProtocolsError(false);
  };

  return {
    protocolOptions,
    protocolsLoading,
    protocolsError,
    fetchMustBeMetProtocols,
    getProtocolLabelByValue,
    getMustBeMetContacts,
    isOnMasterSchoolList,
    resetProtocolOptions,
  };
};

const buildSchoolContactQueryParams = (accountLocationId, placeId): SchoolContactsQueryParams => {
  const params: SchoolContactsQueryParams = { page: 1, perPage: 10 };

  if (accountLocationId) {
    params.accountLocationId = accountLocationId;
  } else if (placeId) {
    params.placeId = placeId;
  }

  return params;
};

const otherLocationTypeContacts = async (
  schoolRecords,
  riderSlug
): Promise<{ contacts: Contact[]; defaultIndividualGroup: AuthorizedContactGroup | null }> => {
  if (schoolRecords.length > 0) {
    const schoolContacts = schoolRecords.map((contact) => {
      return { firstName: contact.name, lastName: '', mobilePhoneNumber: contact.phone };
    });

    return {
      contacts: schoolContacts,
      defaultIndividualGroup: {
        name: schoolContacts[0]?.firstName,
        escalationPhoneNumber: schoolContacts[0]?.mobilePhoneNumber,
        saved: true,
        verifyIdBadge: true,
        verifyRiderName: true,
        verifyRiderBirthdate: false,
      },
    };
  }
  if (riderSlug) {
    const response = await getPassengerCaregivers(riderSlug);

    if (response?.type === 'success') {
      const caregiverContacts = response.data?.caregiverContacts;

      return {
        contacts: caregiverContacts,
        defaultIndividualGroup: null,
      };
    }
  }

  return {
    contacts: [],
    defaultIndividualGroup: null,
  };
};
